import React from "react";
import { Home, Map } from "lucide-react";
import {
  CommunicationIcon,
  MonitorIcon,
  ObservatorioIcon,
  ReportIcon,
  NotificationIcon,
  ForecastIcon,
  SilentNotificationIcon,
  UserIcon,
  LightbulbIcon
} from "@ui";
import { NavigationItem } from "./NavigationItem";
import { useMessageCounter } from "@shared/contexts/MessageCounterContext";
import { useFeatureFlag } from "@shared/hooks";
import { ROUTES } from "@shared/constants/routes.constants";
import { useCanManageUsers, useMultipleScopeCheck } from "@feature/auth";

interface NavigationProps {
  isOpen: boolean;
  isMobile?: boolean;
  onItemClick?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ isOpen, isMobile = false, onItemClick }) => {
  const { unreadCount, pluvialAlertsCount } = useMessageCounter();
  const canManageUsers = useCanManageUsers();
  const isGuidesEnabled = useFeatureFlag("guides");
  const canAccessCommunication = useMultipleScopeCheck(
    isGuidesEnabled ? ["read:alert", "read:citizen-report", "manage:guide"] : ["read:alert", "read:citizen-report"],
  );
  
  const navigationItems = [
    {
      icon: NotificationIcon,
      label: "Notificaciones",
      path: ROUTES.MAIN.NOTIFICATIONS,
      colorActive: "#ffe6fc",
      requiredScope: null,
      bullet: unreadCount,
    },
    {
      icon: Home,
      label: "Inicio",
      path: ROUTES.MAIN.HOME,
      colorActive: "#abeaf6",
      requiredScope: null,
    },
    {
      icon: MonitorIcon,
      label: "Monitor de riesgos",
      path: ROUTES.MAIN.MONITOR,
      colorActive: "#ffe4b2",
      requiredScope: null,
      bullet: pluvialAlertsCount,
    },
    {
      icon: Map,
      label: "Mapa interactivo",
      path: ROUTES.MAIN.MAP,
      colorActive: "#ffe4b2",
      requiredScope: null,
    },
    {
      icon: CommunicationIcon,
      label: "Comunicación ciudadana",
      path: ROUTES.COMMUNICATION.ROOT,
      colorActive: "#edccff",
      requiredScope: "read:alert" as const,
      accessGranted: canAccessCommunication,
    },
    {
      icon: ObservatorioIcon,
      label: "Observatorio",
      path: ROUTES.OBSERVATORY.ROOT,
      colorActive: "#abeaf6",
      requiredScope: null,
    },
    {
      icon: ReportIcon,
      label: "Reportes",
      path: ROUTES.REPORTS.ROOT,
      colorActive: "#ffe6fc",
      requiredScope: null,
    },
    {
      icon: ForecastIcon,
      label: "SIGEM",
      path: "https://sigem.jaliscoalerta.com/",
      colorActive: "#ffe6fc",
      requiredScope: null,
    },
    {
      icon: LightbulbIcon,
      label: "Aprende",
      path: import.meta.env.VITE_APRENDE_URL,
      colorActive: "#ffe6fc",
      requiredScope: null,
      accessGranted: Boolean(import.meta.env.VITE_APRENDE_URL),
    },
    {
      icon: UserIcon,
      label: "Usuarios",
      path: ROUTES.MAIN.USERS,
      colorActive: "#ffe6fc",
      requiredScope: null,
      accessGranted: canManageUsers,
    },
    {
      icon: SilentNotificationIcon,
      label: "Notificaciones silenciosas",
      path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.SILENT,
      colorActive: "#ffe6fc",
      requiredScope: "all:silent" as const,
    },
    {
      icon: SilentNotificationIcon,
      label: "Silenciosas pronósticos",
      path: ROUTES.COMMUNICATION.CREATE_MULTIPLE_FORECAST,
      colorActive: "#ffe6fc",
      requiredScope: "all:silent" as const,
    },
  ];

  return (
    <nav className="flex-1 p-4">
      <ul className="space-y-1">
        {navigationItems.map((item) => (
          <NavigationItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            path={item.path}
            colorActive={item.colorActive}
            requiredScope={item.requiredScope}
            accessGranted={'accessGranted' in item ? item.accessGranted : null}
            isOpen={isOpen}
            bullet={item.bullet}
            isMobile={isMobile}
            onItemClick={onItemClick}
          />
        ))}
      </ul>
    </nav>
  );
};
